import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAdminCosts } from "@/features/admin";
import type { CostBuckets, CostsDashboard } from "@/features/admin";
import { useState } from "react";
import { AdminPageLayout } from "../layout";
import { PeriodSelect, SHORT_PERIODS } from "./components/period-select";
import { StatCard } from "./components/stat-card";

/**
 * What the platform costs, read from the usage ledger and priced with the price list in
 * the backend config (`pricing`). Every figure is an estimate: the ledger stores units
 * (tokens, characters, audio, minutes of line) and the prices are applied at read time, so
 * a corrected price corrects the whole history. Anything the price list does not cover is
 * listed as unpriced, never shown as free.
 */

const num = (n: number) => Math.round(n).toLocaleString("fr-FR");
const pct = (n: number) => `${(n * 100).toFixed(1)} %`;
const minutes = (ms: number) =>
  `${(ms / 60_000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} min`;

const CHANNEL_LABELS: Record<string, string> = {
  call: "Appel téléphonique",
  web_voice: "Widget web (voix)",
  text: "Chat écrit",
  none: "Hors conversation",
};

const KIND_LABELS: Record<string, string> = {
  llm: "LLM",
  embedding: "Embeddings",
  tts: "Synthèse vocale",
  stt: "Transcription",
  telephony: "Ligne",
};

const PHASE_LABELS: Record<string, string> = {
  conversation: "Conversation",
  hook: "Relances (« Mmm… »)",
  hook_cache: "Relances — génération du cache",
  forced_answer: "Réponse forcée après outil",
  tool_filler: "Phrase pendant un outil",
  wait_phrase: "Phrase d'attente",
  transfer_check: "Vérification de transfert",
  hold_notice: "Annonce d'attente (transfert)",
  wrapup: "Clôture (titre, résumé, rattrapage)",
  faq_review: "Revue FAQ",
  messages_review: "Revue Messagerie",
  post_call_sms: "SMS après appel",
  prepare: "Préparation de relance",
  onboarding: "Onboarding",
  enrichment: "Enrichissement",
  rag_index: "Indexation RAG",
  rag_search: "Recherche RAG",
  rag_metadata: "Métadonnées RAG",
  qualification_form: "Génération de formulaire",
  qualification_scoring: "Scoring de qualification",
  callback_preferences: "Préférences de rappel",
  reminder: "Rappel programmé",
  voice_sample: "Échantillon de voix",
  synthetic_caller: "Appel de test (robot)",
  json_generator: "Générateur JSON",
  unknown: "Non attribué",
};

const phaseLabel = (phase: string) =>
  phase.startsWith("tool:")
    ? `Outil ${phase.slice(5)}`
    : PHASE_LABELS[phase] || phase;

/**
 * The per-minute slices, in the order a minute is lived: line, ears, voice, brain.
 * On the chart tokens, with the same slice in the same colour on every bar of the
 * page (the caller's voice is `--chart-1` wherever it appears); what is overhead
 * rather than spend — the line, the leftovers — stays grey.
 */
const BUCKETS: {
  key: keyof CostBuckets;
  label: string;
  color: string;
}[] = [
  { key: "telephony", label: "Ligne", color: "bg-slate-400" },
  { key: "stt", label: "Transcription", color: "bg-chart-1" },
  { key: "tts", label: "Synthèse vocale", color: "bg-chart-2" },
  { key: "llm_live", label: "LLM pendant l'appel", color: "bg-chart-5" },
  { key: "llm_after", label: "LLM après l'appel", color: "bg-chart-4" },
  { key: "llm_other", label: "LLM autre", color: "bg-muted-foreground/40" },
];

/** A horizontal bar split into labelled shares, with its legend. */
function SharesBar({
  parts,
  format,
}: {
  parts: { label: string; value: number; color: string }[];
  format: (value: number) => string;
}) {
  const total = parts.reduce((sum, part) => sum + Math.max(0, part.value), 0);
  if (total <= 0) {
    return <p className="text-sm text-muted-foreground">Aucune donnée.</p>;
  }
  return (
    <div className="space-y-2">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
        {parts.map((part) =>
          part.value > 0 ? (
            <div
              key={part.label}
              className={part.color}
              style={{ width: `${(part.value / total) * 100}%` }}
              title={`${part.label} : ${format(part.value)}`}
            />
          ) : null,
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {parts.map((part) => (
          <span key={part.label} className="flex items-center gap-1.5">
            <span
              className={`inline-block h-2 w-2 rounded-full ${part.color}`}
            />
            <span className="text-muted-foreground">{part.label}</span>
            <span className="tabular-nums">
              {format(part.value)} · {pct(Math.max(0, part.value) / total)}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function AdminCostsPage() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useAdminCosts(days);

  const money = (value: number | null | undefined, digits = 2) =>
    value === null || value === undefined
      ? "—"
      : value.toLocaleString("fr-FR", {
          style: "currency",
          currency: data?.currency || "USD",
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        });
  const precise = (value: number | null | undefined) => money(value, 4);

  const call = data?.channels.find((c) => c.channel === "call");
  const web = data?.channels.find((c) => c.channel === "web_voice");

  return (
    <AdminPageLayout
      title="Coûts"
      description="Estimation à partir des unités consommées (tokens, caractères, audio, minutes de ligne) et de la grille de prix de la config — tous tenants confondus"
      actions={[
        <PeriodSelect
          days={days}
          onChange={setDays}
          periods={SHORT_PERIODS}
        />,
      ]}
    >
      <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard
          title="Coût total sur la période"
          value={money(data?.total_cost)}
          isLoading={isLoading}
        />
        <StatCard
          title="Coût d'une minute d'appel"
          value={precise(call?.cost_per_minute)}
          hint={
            call
              ? `${minutes(call.talk.line_ms)} sur ${num(call.sessions)} appels`
              : undefined
          }
          isLoading={isLoading}
        />
        <StatCard
          title="Coût d'une minute de widget vocal"
          value={precise(web?.cost_per_minute)}
          hint={
            web
              ? `${minutes(web.talk.line_ms)} sur ${num(web.sessions)} sessions`
              : undefined
          }
          isLoading={isLoading}
        />
        <StatCard
          title="Consommations sans tarif"
          value={num(data?.unpriced.reduce((sum, u) => sum + u.rows, 0) || 0)}
          hint="lignes non chiffrées — voir en bas"
          isLoading={isLoading}
          tone={data?.unpriced.length ? "warn" : "default"}
        />
      </section>

      {data?.channels
        .filter((c) => c.minutes > 0)
        .map((channel) => (
          <ChannelSection
            key={channel.channel}
            channel={channel}
            money={precise}
          />
        ))}

      {data ? <SpeechSection data={data} money={money} /> : null}

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Par phase — ce que chaque étape coûte, et pour quel canal
        </h3>
        <Card>
          <CardContent className="pt-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="pb-2 text-left font-medium">Phase</th>
                  <th className="pb-2 text-left font-medium">Type</th>
                  <th className="pb-2 text-left font-medium">Canal</th>
                  <th className="pb-2 text-right font-medium">Appels API</th>
                  <th className="pb-2 text-right font-medium">Sessions</th>
                  <th className="pb-2 text-right font-medium">Coût</th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_phase || []).map((row) => (
                  <tr
                    key={`${row.kind}:${row.phase}:${row.channel}`}
                    className="border-t"
                  >
                    <td className="py-2">{phaseLabel(row.phase)}</td>
                    <td className="py-2 text-muted-foreground">
                      {KIND_LABELS[row.kind] || row.kind}
                    </td>
                    <td className="py-2 text-muted-foreground">
                      {CHANNEL_LABELS[row.channel] || row.channel}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {num(row.rows)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {num(row.sessions)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {money(row.cost)}
                      {row.unpriced_rows ? (
                        <span className="ml-1 text-amber-600">*</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Par modèle — les unités consommées et leur prix
        </h3>
        <Card>
          <CardContent className="pt-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="pb-2 text-left font-medium">Modèle</th>
                  <th className="pb-2 text-right font-medium">Appels</th>
                  <th className="pb-2 text-right font-medium">Entrée</th>
                  <th className="pb-2 text-right font-medium">
                    Cache lu / écrit
                  </th>
                  <th className="pb-2 text-right font-medium">Sortie</th>
                  <th className="pb-2 text-right font-medium">
                    Caractères / audio
                  </th>
                  <th className="pb-2 text-right font-medium">Coût</th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_model || []).map((row) => (
                  <tr
                    key={`${row.kind}:${row.provider}:${row.model}`}
                    className="border-t"
                  >
                    <td className="py-2">
                      <span className="text-muted-foreground">
                        {KIND_LABELS[row.kind] || row.kind} ·{" "}
                      </span>
                      <span className="font-medium">{row.provider}</span>
                      {row.model ? (
                        <span className="text-muted-foreground">
                          {" "}
                          · {row.model}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {num(row.rows)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {row.input_tokens ? num(row.input_tokens) : "—"}
                    </td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">
                      {row.cache_read_tokens || row.cache_write_tokens
                        ? `${num(row.cache_read_tokens)} / ${num(row.cache_write_tokens)}`
                        : "—"}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {row.output_tokens ? num(row.output_tokens) : "—"}
                    </td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">
                      {row.characters
                        ? `${num(row.characters)} car.`
                        : row.audio_ms
                          ? minutes(row.audio_ms)
                          : "—"}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {row.unpriced_rows ? (
                        <span className="text-amber-600">sans tarif</span>
                      ) : (
                        money(row.cost)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Par client
        </h3>
        <Card>
          <CardContent className="pt-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="pb-2 text-left font-medium">Client</th>
                  <th className="pb-2 text-right font-medium">
                    Minutes d'appel
                  </th>
                  <th className="pb-2 text-right font-medium">Coût</th>
                  <th className="pb-2 text-right font-medium">
                    Coût / min d'appel
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_client || []).map((row) => (
                  <tr key={row.client_id} className="border-t">
                    <td className="py-2">{row.name}</td>
                    <td className="py-2 text-right tabular-nums">
                      {row.call_minutes.toLocaleString("fr-FR", {
                        maximumFractionDigits: 1,
                      })}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {money(row.cost)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">
                      {row.call_minutes > 0
                        ? precise(row.cost / row.call_minutes)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {data?.unpriced.length ? (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">
            Sans tarif — à ajouter à <code>pricing</code> dans la config backend
          </h3>
          <Card>
            <CardContent className="pt-6 text-sm">
              <ul className="space-y-1">
                {data.unpriced.map((u) => (
                  <li key={`${u.kind}:${u.provider}:${u.model}`}>
                    <span className="text-muted-foreground">
                      {KIND_LABELS[u.kind] || u.kind} ·{" "}
                    </span>
                    {u.provider}
                    {u.model ? ` · ${u.model}` : ""}
                    <span className="text-muted-foreground">
                      {" "}
                      — {num(u.rows)} lignes
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>
      ) : null}
    </AdminPageLayout>
  );
}

/** One voice channel: what a minute costs, where it goes, and who held the line. */
function ChannelSection({
  channel,
  money,
}: {
  channel: CostsDashboard["channels"][number];
  money: (value: number | null | undefined) => string;
}) {
  const talk = channel.talk;
  const silence = Math.max(0, talk.line_ms - talk.caller_ms - talk.agent_ms);
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-muted-foreground">
        {CHANNEL_LABELS[channel.channel] || channel.channel} — une minute de
        ligne, tout compris ({money(channel.cost_per_minute)} / min)
      </h3>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground">
            Où va une minute — coût par minute de ligne, clôture comprise
          </CardTitle>
        </CardHeader>
        <CardContent>
          <SharesBar
            parts={BUCKETS.map((bucket) => ({
              label: bucket.label,
              value: channel.breakdown_per_minute?.[bucket.key] || 0,
              color: bucket.color,
            }))}
            format={(value) => money(value)}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground">
            Qui occupe la ligne ({num(talk.sessions)} sessions clôturées sur{" "}
            {num(channel.sessions)})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <SharesBar
            parts={[
              {
                label: "Le client parle",
                value: talk.caller_ms,
                color: "bg-chart-1",
              },
              {
                label: "L'agent parle",
                value: talk.agent_ms,
                color: "bg-chart-2",
              },
              {
                label: "Silence, attente",
                value: silence,
                color: "bg-muted-foreground/40",
              },
            ]}
            format={minutes}
          />
          <p className="text-xs text-muted-foreground">
            Transcription :{" "}
            {money(minuteCost(channel.breakdown.stt, talk.caller_ms))} par
            minute où le client parle. Synthèse :{" "}
            {money(minuteCost(channel.breakdown.tts, talk.agent_ms))} par minute
            où l'agent parle. Le temps de parole vient des sessions déjà
            clôturées ; les relances et les annonces d'attente n'y figurent pas.
          </p>
        </CardContent>
      </Card>
    </section>
  );
}

const minuteCost = (cost: number, ms: number) =>
  ms > 0 ? cost / (ms / 60_000) : null;

/** Where the speech bill goes: what the STT actually hears, and what the TTS says. */
function SpeechSection({
  data,
  money,
}: {
  data: CostsDashboard;
  money: (value: number | null | undefined) => string;
}) {
  const stt = data.stt;
  const overhead =
    stt.audio_ms > 0 ? (stt.context_ms + stt.preroll_ms) / stt.audio_ms : 0;
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-muted-foreground">
        Transcription et synthèse
      </h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              Transcription — {num(stt.streams)} flux, {minutes(stt.audio_ms)}{" "}
              envoyées, {money(stt.cost)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <SharesBar
              parts={[
                {
                  label: "Voix du client",
                  value: stt.speech_ms,
                  color: "bg-chart-1",
                },
                {
                  label: "Contexte ajouté (fin de réplique + marqueur)",
                  value: stt.context_ms,
                  color: "bg-chart-5",
                },
                {
                  label: "Pré-roll",
                  value: stt.preroll_ms,
                  color: "bg-slate-400",
                },
              ]}
              format={minutes}
            />
            <p className="text-xs text-muted-foreground">
              Chaque flux s'ouvre sur la fin de la dernière réplique de l'agent
              et le marqueur de séparation, puis le son capté juste avant la
              voix : {pct(overhead)} de l'audio facturé n'est pas la voix du
              client.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              Synthèse vocale — {num(data.tts.syntheses)} synthèses,{" "}
              {money(data.tts.cost)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div>
              {num(data.tts.characters)} caractères envoyés,{" "}
              {minutes(data.tts.audio_ms)} d'audio produites
            </div>
            <p className="text-xs text-muted-foreground">
              Compté à l'envoi au fournisseur : une réponse synthétisée puis
              remplacée par une transcription plus complète, ou coupée par le
              client, est payée même si elle n'a jamais été entendue.
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
